import moment from 'moment';
import React, { useState } from 'react';
import { IoArrowDown } from 'react-icons/io5';
import { Dropdown, Modal, Select, Textarea } from 'rizzui';
import { toast } from 'sonner';
import { StateOption, StateOptions } from '../../type/entities/state-type';
import { KoReasonOption } from '../../type/entities/ko-reason-type';
import { filterKoReasonsByStatusEntity } from '../../hooks/api-calls/useKoReasons';
import cn from '../../utils/classNames';
import { CustomBadge } from './CustomBadge';

interface StatusDropdownProps {
    entityId: string;
    currentState: any;
    currentStateDate?: string;
    statesOptions: StateOptions;
    title: string;
    dropdownClassName?: string;
    disabled?: boolean;
    badgeSize?: "sm" | "md" | "lg" | "xl" | undefined;
    badgeRounded?: "none" | "sm" | "md" | "lg" | "pill" | undefined;
    modalSize?: 'sm' | 'md' | 'lg' | 'full';
    cancelBtnText?: string;
    confirmBtnText?: string;
    handleStateChange: (entityId: string, newStateId: string, comment: string, koReasonId?: string) => void;
    hasComments?: boolean;
    koReasonsOptions?: KoReasonOption[];
    koReasonStatusEntity?: string;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
    title,
    statesOptions = [],
    entityId,
    currentState,
    currentStateDate,
    dropdownClassName,
    disabled = false,
    badgeSize = 'lg',
    badgeRounded = 'md',
    modalSize = 'md',
    cancelBtnText = 'Cancelar',
    confirmBtnText = 'Confirmar',
    handleStateChange,
    hasComments = true,
    koReasonsOptions = [],
    koReasonStatusEntity,
}) => {

    const filteredKoReasonsOptions = filterKoReasonsByStatusEntity(koReasonsOptions, koReasonStatusEntity);

    // STATES

    const [showModal, setShowModal] = useState(false);
    const [selectedState, setSelectedState] = useState<StateOption | null>(statesOptions.find((state) => state.value === currentState?.id) || null);
    const [selectedKoReasonId, setSelectedKoReasonId] = useState<string>('');

    // Mostrar selector de motivo KO cuando el estado tiene requiresKoReason activo (motivo de KO activado en admin)
    const selectedStateRequiresKoReason = Boolean(selectedState?.requiresKoReason);

    // ---------------------------------------------------
    // DROPDOWN MENU ACTIONS
    // ---------------------------------------------------
    const handleStateSelect = (state: StateOption) => {
        setSelectedState(state);
        setSelectedKoReasonId('');
        setShowModal(true);
    };

    // ---------------------------------------------------
    // MODAL MENU ACTIONS
    // ---------------------------------------------------

    const handleConfirm = () => {
        const textareaValue = document.getElementById('comments') as HTMLInputElement;
        const comment = textareaValue?.value || '';

        if (selectedStateRequiresKoReason) {
            if (filteredKoReasonsOptions.length === 0) {
                toast.error('No hay motivos de KO configurados para este tipo de estado. Contacte al administrador.');
                return;
            }
            if (!selectedKoReasonId) {
                toast.error('Debe seleccionar un motivo de KO');
                return;
            }
        }

        setShowModal(false);
        if (selectedState) {
            handleStateChange(entityId, selectedState.value, comment, selectedStateRequiresKoReason ? selectedKoReasonId : undefined);
        }
    };

    const handleCancel = () => {
        setSelectedState(statesOptions.find((state) => state.value === currentState.id) || null);
        setShowModal(false);
    };

    // RENDER

    return (
        <>
            <Dropdown placement="bottom-end" className={cn('w-full', dropdownClassName)} key={entityId}>
                <Dropdown.Trigger className='w-full'>
                    <CustomBadge
                        className={cn('w-full p-0', disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
                        rounded={badgeRounded}
                        size={badgeSize}
                        customColor={selectedState?.color || currentState?.color || 'primary'}
                    >
                        <div className="flex flex-col justify-center items-center text-nowrap leading-relaxed m-1">
                            {selectedState?.label || currentState?.name || 'Seleccionar Estado'} <br />
                            {currentStateDate && (moment(currentStateDate).format('DD/MM/YY HH:mm'))}
                        </div>
                    </CustomBadge>
                </Dropdown.Trigger>
                {!disabled && (
                    <Dropdown.Menu className="!max-h-[300px] w-auto !overflow-y-auto shadow-xl">
                        {statesOptions && statesOptions.length >= 1 && statesOptions.map((state) => {
                            // Filter out the current state from the dropdown options
                            if (state.value === currentState?.id) return null;
                            return (
                                <Dropdown.Item key={state.value} onClick={() => handleStateSelect(state)} className='w-full' >
                                    <CustomBadge className="cursor-pointer w-full" rounded={badgeRounded} size={badgeSize || 'lg'} customColor={state.color || 'primary'} >
                                        {state.label}
                                    </CustomBadge>
                                </Dropdown.Item>
                            )
                        })}
                        {(!statesOptions || statesOptions.length === 0) && (
                            <div className='px-4 py-2'>Sin resultados...</div>
                        )}
                    </Dropdown.Menu>
                )}
            </Dropdown>

            {showModal && (
                <Modal isOpen={showModal} onClose={() => { }} size={modalSize}>
                    <div className='flex flex-col justify-center p-2 pt-4'>
                        <h4 className="text-2xl text-center">{title || '¿Estás seguro de cambiar el estado?'}</h4>
                    </div>
                    <div className='flex flex-col gap-3 my-3'>
                        <CustomBadge
                            className="cursor-pointer m-auto"
                            rounded={badgeRounded}
                            size={badgeSize}
                            customColor={currentState?.color || 'primary'}
                        >
                            {currentState?.name || 'Seleccionar Estado'}
                        </CustomBadge>
                        <IoArrowDown className="text-md text-black m-auto" />
                        <CustomBadge
                            className="cursor-pointer m-auto"
                            rounded={badgeRounded}
                            size={badgeSize}
                            customColor={selectedState?.color || 'primary'}
                        >
                            {selectedState?.label || 'Seleccionar Estado'}
                        </CustomBadge>
                    </div>
                    {selectedStateRequiresKoReason && (
                        <div className="p-4">
                            {filteredKoReasonsOptions.length > 0 ? (
                                <Select
                                    label="Motivo de KO"
                                    placeholder="Selecciona un motivo"
                                    options={filteredKoReasonsOptions}
                                    value={selectedKoReasonId ? { value: selectedKoReasonId, label: filteredKoReasonsOptions.find(o => o.value === selectedKoReasonId)?.label || '' } : null}
                                    onChange={(option: any) => setSelectedKoReasonId(option?.value || '')}
                                    className="[&>label>span]:font-medium"
                                />
                            ) : (
                                <p className="text-amber-600 text-sm">Este estado requiere un motivo de KO. No hay motivos configurados. Contacte al administrador.</p>
                            )}
                        </div>
                    )}
                    {hasComments && (
                        <Textarea
                            id="comments"
                            label="Comentario"
                            placeholder="Escribe un comentario"
                            rows={3}
                            className="col-span-1 md:col-span-2 [&>label>span]:font-medium p-4"
                        />
                    )}

                    <div className="flex justify-center p-3 gap-4">
                        <button className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-300" onClick={handleConfirm}>
                            {confirmBtnText}
                        </button>
                        <button className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600" onClick={handleCancel}>
                            {cancelBtnText}
                        </button>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default StatusDropdown;